import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Rule } from './Rule'

/**
 * Sección de una vista: enunciado + bajada en prosa + acciones + regla.
 *
 * El encabezado es un **enunciado**, no un rótulo: "Lo que frena" en vez de
 * "Portfolio". La bajada da la conclusión antes de la tabla, así quien lee el
 * panel ya sabe qué mirar cuando llega a los números. Los números de la bajada
 * van en `<b>`: prosa en gris, cifras en negro. El contraste de peso hace saltar
 * los números sin agregar un color.
 *
 * Es la primitiva que hace que las otras pantallas salgan sin CSS nuevo: seis
 * módulos componen secciones, no layouts a medida.
 */
export interface SectionProps {
  /** El enunciado. Da la conclusión, no el nombre del dataset. */
  titulo: string
  /** Bajada en prosa. Poné las cifras en `<b>`. */
  bajada?: ReactNode
  /** Rótulo chico arriba del título, ej. "ZONA 1". */
  kicker?: string
  /** Acciones de nivel sección, alineadas a la derecha del encabezado. */
  acciones?: ReactNode
  /** Nivel del heading. 2 por defecto; 3 para una sección anidada. */
  nivel?: 2 | 3
  /** false quita la regla bajo el encabezado. */
  regla?: boolean
  /** Aire debajo de la sección. */
  espaciado?: 'sm' | 'md' | 'lg'
  id?: string
  className?: string
  children: ReactNode
}

const ESPACIADO: Record<NonNullable<SectionProps['espaciado']>, string> = {
  sm: 'mb-5',
  md: 'mb-8',
  lg: 'mb-12',
}

export function Section({
  titulo,
  bajada,
  kicker,
  acciones,
  nivel = 2,
  regla = true,
  espaciado = 'md',
  id,
  className,
  children,
}: SectionProps) {
  const Heading = nivel === 2 ? 'h2' : 'h3'

  return (
    <section
      id={id}
      aria-label={titulo}
      data-testid="section"
      className={cx('min-w-0', ESPACIADO[espaciado], className)}
    >
      <header className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          {kicker !== undefined ? (
            <p className="rotulo mb-1.5">{kicker}</p>
          ) : null}
          <Heading
            className={cx(
              'text-text',
              nivel === 2 ? 'text-seccion' : 'text-titulo',
            )}
          >
            {titulo}
          </Heading>
          {bajada !== undefined ? (
            <p
              data-testid="section-bajada"
              className="mt-1.5 max-w-prosa text-meta leading-relaxed text-muted-strong [&_b]:font-semibold [&_b]:text-text"
            >
              {bajada}
            </p>
          ) : null}
        </div>
        {acciones !== undefined ? (
          <div className="flex flex-wrap items-center gap-2">{acciones}</div>
        ) : null}
      </header>

      {regla ? <Rule spacing="sm" /> : null}
      {children}
    </section>
  )
}
